import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../base';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { doc, updateDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../base';
import { toast } from 'react-toastify';
import { useOnboarding } from '../contexts/OnboardingContext';
import { useProjectLimits } from '../hooks/useProjectLimits';
import './SuccessPage.css';

const PENDING_ONBOARDING_REQUEST_KEY = 'facade_pending_onboarding_request';
const PENDING_POST_PURCHASE_RETURN_KEY = 'facade_pending_post_purchase_return';

function SuccessPage({ setIsAccountModalOpen }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const currentUser = useAuth();
    const [loading, setLoading] = useState(true);
    const [sessionData, setSessionData] = useState(null);
    const [currentUserData, setCurrentUserData] = useState(null);
    const [postPurchaseOnboardingPrepared, setPostPurchaseOnboardingPrepared] = useState(false);
    const { restartOnboarding } = useOnboarding();
    const { projectCount, isLoading: projectCountLoading } = useProjectLimits(currentUser, currentUserData);
    const sessionId = searchParams.get('session_id');

    useEffect(() => {
        let mounted = true;
        let unsubscribeUser = null;

        const initializeSuccess = async () => {
            if (!currentUser || !sessionId) {
                setLoading(false);
                return;
            }

            try {
                // 1. Fetch Stripe session data to get purchase details
                const sessionResponse = await fetch('/.netlify/functions/get-checkout-session', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ sessionId })
                });

                let sessionInfo = null;
                if (sessionResponse.ok) {
                    sessionInfo = await sessionResponse.json();
                    if (mounted) {
                        setSessionData(sessionInfo);
                    }

                    const isEligibleUser = Boolean(currentUser?.uid && currentUser?.email && !currentUser.email.startsWith('guest_'));
                    const checkoutEmail = (sessionInfo.customer_details?.email || '').trim().toLowerCase();
                    const accountEmail = (currentUser?.email || '').trim().toLowerCase();
                    const metadataUserId = sessionInfo.metadata?.userId || null;

                    if (
                        isEligibleUser &&
                        sessionInfo.id &&
                        sessionInfo.payment_status === 'paid' &&
                        checkoutEmail &&
                        checkoutEmail === accountEmail
                    ) {
                        const claimResponse = await fetch('/.netlify/functions/claim-checkout-session', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                                sessionId: sessionInfo.id,
                                userId: currentUser.uid,
                                userEmail: currentUser.email,
                            })
                        });

                        if (!claimResponse.ok) {
                            const claimResult = await claimResponse.json().catch(() => ({}));
                            throw new Error(claimResult.error || 'Unable to restore this purchase to your account.');
                        }
                    } else if (
                        isEligibleUser &&
                        sessionInfo.id &&
                        sessionInfo.payment_status === 'paid' &&
                        metadataUserId === currentUser.uid
                    ) {
                        console.warn('Skipping checkout claim because Stripe checkout email does not match the signed-in account email.', {
                            sessionId: sessionInfo.id,
                            accountEmail,
                            checkoutEmail,
                        });
                    }
                }

                // Function to ensure plan data is updated if webhook missed it
                const ensurePlanDataUpdated = async (userDocRef, sessionData, currentUserData) => {
                    try {
                        const { metadata } = sessionData;
                        if (!metadata || !metadata.planName) {
                            console.log('No metadata or planName in session');
                            return;
                        }

                        const planName = metadata.planName;
                        const isStandardPlan = planName === 'standard_plan';
                        const isProPlan = planName === 'pro_plan';
                        const isSubscription = !!(sessionData.subscription);

                        // Check if this is a subscription that should replace current plan
                        if ((isStandardPlan || isProPlan) && isSubscription) {
                            // Check if plan data is already updated
                            const needsUpdate = currentUserData?.currentPlan !== planName || 
                                               currentUserData?.planName !== planName ||
                                               currentUserData?.planType !== planName ||
                                               currentUserData?.isSubscription !== true;

                            if (needsUpdate) {
                                console.log('Success page updating plan data for:', planName);
                                
                                const updateData = {
                                    currentPlan: planName,
                                    planName: planName,
                                    planType: planName,
                                    isSubscription: true,
                                    subscriptionId: sessionData.subscription,
                                    subscriptionStatus: 'active',
                                    planPrice: `$${(sessionData.amount_total / 100).toFixed(2)}`,
                                    billingCycle: 'monthly',
                                    lastUpdated: new Date()
                                };

                                await updateDoc(userDocRef, updateData);
                                console.log('Plan data updated successfully');
                                toast.success(t('payment.planUpdated'));
                            }
                        }
                    } catch (error) {
                        console.error('Error ensuring plan data updated:', error);
                    }
                };

                // 2. Set up real-time listener for user data updates
                const userDocRef = doc(db, 'users', currentUser.uid);
                
                unsubscribeUser = onSnapshot(userDocRef, async (docSnapshot) => {
                    if (!mounted) return;
                    
                    if (docSnapshot.exists()) {
                        const data = docSnapshot.data();
                        setCurrentUserData(data);
                        
                        // Check if plan data needs updating based on session
                        if (sessionInfo && sessionInfo.metadata) {
                            await ensurePlanDataUpdated(userDocRef, sessionInfo, data);
                        }
                    }
                    
                    setLoading(false);
                }, (error) => {
                    console.error('Error listening to user data:', error);
                    setLoading(false);
                });

            } catch (error) {
                console.error('Error initializing success page:', error);
                if (mounted) setLoading(false);
            }
        };

        initializeSuccess();

        return () => {
            mounted = false;
            if (unsubscribeUser) {
                unsubscribeUser();
            }
        };
    }, [currentUser, sessionId, t]);

    const shouldStartFirstProjectOnboarding = () => {
        const purchaseType = sessionData?.metadata?.purchaseType || 'plan';
        const planName = sessionData?.metadata?.planName || '';
        const knownProjectIds = currentUserData?.projectIds;
        const hasKnownProjectIds = Array.isArray(knownProjectIds);
        const computedShouldStart = (() => {
            if (purchaseType !== 'plan' || !planName || planName === 'extra_generation_credits_pack') {
                return false;
            }

            if (sessionData?.payment_status !== 'paid') {
                return false;
            }

            if (!projectCountLoading) {
                return projectCount === 0;
            }

            if (hasKnownProjectIds) {
                return knownProjectIds.length === 0;
            }

            return true;
        })();

        return computedShouldStart;
    };

    useEffect(() => {
        if (postPurchaseOnboardingPrepared || !shouldStartFirstProjectOnboarding()) {
            return;
        }

        try {
            sessionStorage.setItem(PENDING_POST_PURCHASE_RETURN_KEY, 'true');
            sessionStorage.setItem(PENDING_ONBOARDING_REQUEST_KEY, JSON.stringify({
                flow: 'memberNewProject',
                startStep: 'create-project',
                force: true,
            }));
            setPostPurchaseOnboardingPrepared(true);
        } catch (storageError) {
            console.warn('Failed to persist pending onboarding request:', storageError);
        }
    }, [postPurchaseOnboardingPrepared, sessionData, currentUserData, projectCountLoading, projectCount]);

    const navigateAfterSuccess = (targetPath = '/', options = {}) => {
        const { openAccount = false } = options;
        const shouldStart = shouldStartFirstProjectOnboarding();

        if (shouldStart) {
            restartOnboarding('memberNewProject', 'create-project');
        }

        if (openAccount && setIsAccountModalOpen) {
            setIsAccountModalOpen(true);
        }

        navigate(targetPath);
    };

    const handleGetStarted = () => {
        navigateAfterSuccess('/');
    };

    const handleViewAccount = () => {
        navigateAfterSuccess('/', { openAccount: true });
    };

    const handleClose = () => {
        navigateAfterSuccess('/');
    };

    // Get plan display name for the success message
    const getPlanDisplayName = () => {
        if (!sessionData?.metadata?.planName) return '';
        
        const planName = sessionData.metadata.planName;
        switch(planName) {
            case 'standard_plan':
                return t('Standard Plan');
            case 'pro_plan':
                return t('Pro Plan');
            case 'my_project_plan':
                return t('My Project');
            default:
                return planName;
        }
    };

    const formatPrice = () => {
        if (!sessionData?.amount_total) return '';
        return `$${(sessionData.amount_total / 100).toFixed(2)}`;
    };

    if (loading) {
        return (
            <div className="success-page loading">
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <h2>{t('payment.processing')}</h2>
                    <p>{t('payment.processingMessage')}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="success-page">
            <div className="success-container">
                {/* Close Button */}
                <button 
                    className="close-button"
                    onClick={handleClose}
                    title={t('Close')}
                >
                    ×
                </button>

                <div className="success-icon">
                    <svg width="80" height="80" viewBox="0 0 80 80" fill="none">
                        <circle cx="40" cy="40" r="40" fill="#10B981"/>
                        <path d="M25 40L35 50L55 30" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </div>

                <h1 className="success-title">
                    {sessionData?.metadata?.planName ? 
                        t('payment.success.titleWithPlan', { planName: getPlanDisplayName() }) :
                        t('payment.success.title')
                    }
                </h1>
                <p className="success-message">
                    {sessionData?.metadata?.planName ? 
                        t('payment.success.messageWithDetails', { 
                            planName: getPlanDisplayName(),
                            price: formatPrice()
                        }) :
                        t('payment.success.message')
                    }
                </p>

                <div className="success-details">
                    <div className="detail-item">
                        <span className="detail-icon">✨</span>
                        <span>{t('payment.success.details.fullAccess')}</span>
                    </div>
                    <div className="detail-item">
                        <span className="detail-icon">🚀</span>
                        <span>{t('payment.success.details.aiRecommendations')}</span>
                    </div>
                    <div className="detail-item">
                        <span className="detail-icon">💾</span>
                        <span>{t('payment.success.details.enhancedStorage')}</span>
                    </div>
                    <div className="detail-item">
                        <span className="detail-icon">🎯</span>
                        <span>{t('payment.success.details.prioritySupport')}</span>
                    </div>
                </div>

                <div className="success-actions">
                    <button 
                        className="btn btn-primary btn-lg"
                        onClick={handleGetStarted}
                    >
                        {t('payment.success.actions.startDesigning')}
                    </button>
                    <button 
                        className="btn btn-outline-secondary btn-lg"
                        onClick={handleViewAccount}
                    >
                        {t('payment.success.actions.viewAccount')}
                    </button>
                </div>

                <div className="success-footer">
                    <p>{t('payment.success.actions.needHelp')}</p>
                    <p className="session-info">
                        {t('payment.success.actions.sessionId')}: <code>{sessionId}</code>
                    </p>
                </div>
            </div>
        </div>
    );
}

export default SuccessPage;
